import PptxGenJS from 'pptxgenjs';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import imageService from './imageService';

/*
 * Browser PowerPoint export (pptxgenjs). Used for the legacy PowerPoint template path and for
 * Word-to-slides exports until the worker builds decks from the stored master (later waves).
 *
 * Layout rules (wave 1):
 * - The slide is 16:9, 10 x 5.625 in. Every shape is placed inside that box, with a bottom margin.
 * - Every box has a numeric height computed from its line count and font size (never 'auto').
 * - Content that does not fit continues on a "(continued)" slide; nothing is dropped.
 * - Lists use one PowerPoint bullet per line (no typed bullet glyphs on top).
 * - Colours and fonts come from the template's stored theme when one is passed in.
 */

/** 16:9 slide width in inches (pptxgenjs LAYOUT_16x9). */
export const SLIDE_WIDTH_IN = 10;
/** 16:9 slide height in inches (pptxgenjs LAYOUT_16x9). */
export const SLIDE_HEIGHT_IN = 5.625;
/** Left and right page margin. */
const MARGIN_X_IN = 0.5;
/** Top of the title box. */
const TOP_IN = 0.3;
/** Space kept clear at the bottom of the slide. */
const BOTTOM_MARGIN_IN = 0.3;
/** Lowest y any content may reach. */
export const CONTENT_BOTTOM_IN = SLIDE_HEIGHT_IN - BOTTOM_MARGIN_IN;
/** Width of the content column. */
export const CONTENT_WIDTH_IN = SLIDE_WIDTH_IN - 2 * MARGIN_X_IN;
/** Vertical gap between stacked blocks. */
const BLOCK_GAP_IN = 0.15;
/** Text box inset on every side (inches); passed to pptxgenjs in points. */
const TEXT_INSET_IN = 0.05;
const TEXT_INSET_PT = TEXT_INSET_IN * 72;
/** Extra height added to every measured box so a slightly wider font still fits. */
const HEIGHT_SAFETY_IN = 0.06;
/** Line height as a multiple of the font size (PowerPoint single spacing is about 1.2). */
const LINE_HEIGHT_FACTOR = 1.25;
/** pptxgenjs bullet indent (27pt) per level. */
const BULLET_INDENT_IN = 27 / 72;
/** Space after each list item (points). */
const LIST_SPACE_AFTER_PT = 4;
/** Table cell margins used by pptxgenjs by default (top+bottom, left+right), inches. */
const TABLE_CELL_PAD_Y_IN = 0.1;
const TABLE_CELL_PAD_X_IN = 0.2;
/** Smallest image height worth placing at the bottom of a slide instead of moving it on. */
const MIN_IMAGE_HEIGHT_IN = 1.25;
/** Fallback image box when the real size is unknown (4:3). */
const DEFAULT_IMAGE_ASPECT = 3 / 4;
/** Hard stop for the layout loop (never reached by real content). */
const MAX_LAYOUT_STEPS = 20000;

const BODY_FONT_PT = 14;
const HEADING_FONT_PT = { h1: 20, h2: 18, h3: 16 };
const TABLE_FONT_PT = 11;
const QUOTE_PAD_IN = 0.1;
const QUOTE_BAR_IN = 0.06;

const CONTINUED_SUFFIX = '(continued)';
const PPTX_MIME = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

/** Hard-coded colours, used only when no stored theme (and no explicit option) gives one. */
export const DEFAULT_DECK_COLORS = Object.freeze({
  titleColor: '1a1a1a',
  bodyColor: '333333',
  accentColor: '1976d2',
  backgroundColor: 'ffffff',
});

const BLOCK_TAGS = new Set([
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'table', 'blockquote', 'img', 'div', 'section',
  'article', 'main', 'header', 'footer', 'aside', 'figure', 'nav', 'pre', 'hr',
]);
const CONTAINER_TAGS = new Set(['div', 'section', 'article', 'main', 'header', 'footer', 'aside', 'figure', 'nav']);

/**
 * Typed bullet glyphs at the start of a list item (the list itself already draws a bullet).
 * Symbol glyphs may be followed directly by the text ('•Item'); '*', '-' and the en dash need a
 * space after them, so '-5 degrees' or '*note*' are left alone.
 */
const MANUAL_BULLET_RE = /^[^\S\n]*(?:[•●▪▫◦‣∙·○■□➢➤►▶✓✔][^\S\n]*|[*–-][^\S\n]+)/;
const MANUAL_NUMBER_RE = /^\s*\(?\d{1,3}[.)]\s+/;

// ---------------------------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------------------------

/**
 * Normalises a colour to six lower-case hex digits.
 * @param {unknown} value - '#RRGGBB', 'RRGGBB', '#RGB'.
 * @returns {string|null} Hex without '#', or null when not a usable colour.
 */
export function normalizeHexColor(value) {
  if (typeof value !== 'string') return null;
  let hex = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  return /^[0-9a-f]{6}$/i.test(hex) ? hex.toLowerCase() : null;
}

/**
 * WCAG relative luminance of a hex colour.
 * @param {string} hex - Six hex digits.
 * @returns {number} 0 (black) to 1 (white).
 */
function luminance(hex) {
  const channels = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  const [r, g, b] = channels.map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG contrast ratio between two hex colours.
 * @param {string} a - Hex colour.
 * @param {string} b - Hex colour.
 * @returns {number} 1 to 21.
 */
export function contrastRatio(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * First candidate that reads well on `background`, else the candidate with the best contrast.
 * @param {(string|null|undefined)[]} candidates - Hex colours in order of preference.
 * @param {string} background - Background hex.
 * @param {number} minRatio - Contrast wanted.
 * @returns {string|null} Chosen colour, or null when there are no candidates.
 */
function pickReadable(candidates, background, minRatio) {
  const valid = candidates.map(normalizeHexColor).filter(Boolean);
  if (valid.length === 0) return null;
  const good = valid.find((c) => contrastRatio(c, background) >= minRatio);
  if (good) return good;
  return valid.reduce((best, c) => (contrastRatio(c, background) > contrastRatio(best, background) ? c : best));
}

// ---------------------------------------------------------------------------------------------
// Theme resolution
// ---------------------------------------------------------------------------------------------

/**
 * Typeface name from the many shapes a stored theme font can take.
 * @param {unknown} value - 'Calibri', { latin: 'Calibri' }, { typeface: 'Calibri' }.
 * @returns {string|null} Font name.
 */
function fontName(value) {
  if (typeof value === 'string') {
    const name = value.trim();
    // Theme references such as '+mj-lt' are not real typefaces.
    return name && !name.startsWith('+') ? name : null;
  }
  if (value && typeof value === 'object') {
    return fontName(value.latin) || fontName(value.typeface) || fontName(value.name) || null;
  }
  return null;
}

/**
 * Colour map of a stored theme ({ dk1, lt1, dk2, lt2, accent1..6, hlink, folHlink }).
 * @param {object|null} stored - Stored theme.
 * @returns {Record<string, string>} Normalised hex colours keyed by scheme name.
 */
function themeColorMap(stored) {
  const source = stored && typeof stored === 'object' ? stored.colors || stored.colorScheme || stored.clrScheme : null;
  const out = {};
  if (!source || typeof source !== 'object') return out;
  Object.entries(source).forEach(([key, value]) => {
    const hex = normalizeHexColor(typeof value === 'object' && value ? value.hex || value.srgb || value.val : value);
    if (hex) out[key] = hex;
  });
  return out;
}

/**
 * Heading (major) and body (minor) fonts of a stored theme.
 * @param {object|null} stored - Stored theme.
 * @returns {{ major: string|null, minor: string|null }} Font names.
 */
function themeFonts(stored) {
  if (!stored || typeof stored !== 'object') return { major: null, minor: null };
  const fonts = stored.fonts && typeof stored.fonts === 'object' ? stored.fonts : {};
  const scheme = stored.fontScheme && typeof stored.fontScheme === 'object' ? stored.fontScheme : {};
  return {
    major:
      fontName(fonts.major) || fontName(fonts.majorFont) || fontName(fonts.heading) || fontName(fonts.title) ||
      fontName(scheme.majorFont) || fontName(scheme.major) || fontName(stored.majorFont) || null,
    minor:
      fontName(fonts.minor) || fontName(fonts.minorFont) || fontName(fonts.body) ||
      fontName(scheme.minorFont) || fontName(scheme.minor) || fontName(stored.minorFont) || null,
  };
}

/**
 * Resolves the colours and fonts used by the exporter. Explicit options win, then the template's
 * stored theme (`options.theme`, as saved by the importer: `{ colors: { dk1, lt1, accent1, ... },
 * fonts: { major, minor } }`), then the defaults. Text colours are checked for contrast against
 * the background so an odd theme never produces unreadable text.
 *
 * @param {object} [options] - Export options.
 * @returns {object} Theme tokens: titleColor, bodyColor, accentColor, backgroundColor,
 *   headingColor, tableHeaderFill, tableHeaderText, tableBodyFill, tableBorder, quoteFill,
 *   quoteBar, quoteText, titleFont, bodyFont, fromStoredTheme.
 */
export function resolveDeckTheme(options = {}) {
  const opts = options && typeof options === 'object' ? options : {};
  const stored = opts.theme && typeof opts.theme === 'object' ? opts.theme : null;
  const c = themeColorMap(stored);
  const fonts = themeFonts(stored);

  const backgroundColor = normalizeHexColor(opts.backgroundColor) || c.lt1 || c.bg1 || DEFAULT_DECK_COLORS.backgroundColor;
  const bodyColor =
    normalizeHexColor(opts.bodyColor) ||
    pickReadable([c.dk1, c.tx1, c.dk2, DEFAULT_DECK_COLORS.bodyColor, '000000', 'ffffff'], backgroundColor, 4.5);
  const titleColor =
    normalizeHexColor(opts.titleColor) ||
    pickReadable([c.dk2, c.dk1, c.tx2, DEFAULT_DECK_COLORS.titleColor, bodyColor], backgroundColor, 3);
  const accentColor =
    normalizeHexColor(opts.accentColor) ||
    pickReadable([c.accent1, c.accent2, c.dk2, DEFAULT_DECK_COLORS.accentColor, titleColor], backgroundColor, 3);
  const tableHeaderFill = c.accent1 || accentColor;
  const tableHeaderText = pickReadable([c.lt1, 'ffffff', c.dk1, '000000'], tableHeaderFill, 4.5);
  const quoteFill = c.lt2 && contrastRatio(c.lt2, bodyColor) >= 4.5 ? c.lt2 : null;
  const tableBorder = c.lt2 && contrastRatio(c.lt2, backgroundColor) >= 1.2 ? c.lt2 : 'bfbfbf';

  return {
    titleColor,
    bodyColor,
    accentColor,
    backgroundColor,
    headingColor: accentColor,
    tableHeaderFill,
    tableHeaderText,
    tableBodyFill: backgroundColor,
    tableBorder,
    quoteFill: quoteFill || (contrastRatio('f8f9fa', bodyColor) >= 4.5 ? 'f8f9fa' : backgroundColor),
    quoteBar: normalizeHexColor(c.accent2) || accentColor,
    quoteText: bodyColor,
    titleFont: fontName(opts.titleFontFace) || fonts.major || fontName(opts.fontFace) || null,
    bodyFont: fontName(opts.bodyFontFace) || fontName(opts.fontFace) || fonts.minor || null,
    fromStoredTheme: Boolean(stored && (Object.keys(c).length > 0 || fonts.major || fonts.minor)),
  };
}

// ---------------------------------------------------------------------------------------------
// HTML parsing
// ---------------------------------------------------------------------------------------------

/**
 * Parses document HTML into export sections (headings, paragraphs, lists, tables, images,
 * blockquotes). Wrapper elements (div, section, article, ...) are flattened so a deck wrapped in
 * `<article><section>` still exports block by block.
 * @param {string} html - Document HTML.
 * @returns {object[]} Sections.
 */
function parseHtmlSectionsEnhanced(html) {
  const sections = [];
  if (!html) return sections;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  Array.from(doc.body.children).forEach((element) => {
    sections.push(...parseElementToSections(element));
  });
  return sections;
}

/**
 * True when an element holds block-level children (and so should be flattened).
 * @param {Element} element - DOM element.
 * @returns {boolean} Whether it is a wrapper.
 */
function isBlockContainer(element) {
  return Array.from(element.children).some((child) => BLOCK_TAGS.has(child.tagName.toLowerCase()));
}

/**
 * Sections for one element.
 * @param {Element} element - DOM element.
 * @returns {object[]} Zero or more sections.
 */
function parseElementToSections(element) {
  const tagName = element.tagName.toLowerCase();

  if (CONTAINER_TAGS.has(tagName) && isBlockContainer(element)) {
    const out = [];
    Array.from(element.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        out.push(...parseElementToSections(node));
      } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
        const text = node.textContent.replace(/\s+/g, ' ').trim();
        out.push({ type: 'p', content: [{ text, formatting: {} }], rawText: text, style: {} });
      }
    });
    return out;
  }

  switch (tagName) {
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6': {
      const rawText = normalizeText(element.textContent);
      if (!rawText) return [];
      const type = ['h1', 'h2', 'h3'].includes(tagName) ? tagName : 'h3';
      return [{ type, content: extractTextWithFormatting(element), rawText, style: extractElementStyle(element) }];
    }
    case 'p': {
      const out = [];
      const rawText = normalizeText(element.textContent);
      if (rawText) {
        out.push({ type: 'p', content: extractTextWithFormatting(element), rawText, style: extractElementStyle(element) });
      }
      // Images inside a paragraph used to be thrown away with the empty paragraph.
      element.querySelectorAll('img').forEach((img) => out.push(parseImageElement(img)));
      return out;
    }
    case 'ul':
    case 'ol': {
      const items = parseListItems(element, 0, { counter: 0 });
      return items.length ? [{ type: tagName, items, style: extractElementStyle(element) }] : [];
    }
    case 'table': {
      const table = parseTableElement(element);
      return table.data.length ? [table] : [];
    }
    case 'img':
      return [parseImageElement(element)];
    case 'blockquote': {
      const rawText = normalizeText(element.textContent);
      if (!rawText) return [];
      return [{ type: 'blockquote', content: extractTextWithFormatting(element), rawText, style: extractElementStyle(element) }];
    }
    case 'hr':
    case 'br':
      return [];
    default: {
      const rawText = normalizeText(element.textContent);
      if (!rawText) return [];
      return [{ type: 'text', content: extractTextWithFormatting(element), rawText, style: extractElementStyle(element) }];
    }
  }
}

/**
 * Collapses HTML whitespace.
 * @param {string} text - Raw text.
 * @returns {string} Trimmed text with single spaces.
 */
function normalizeText(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

/**
 * Flattens a (possibly nested) list into items, one per `li`, each with its own text only (the
 * text of nested lists is not repeated in the parent) and its nesting level.
 * @param {Element} listEl - `ul` or `ol`.
 * @param {number} level - Nesting level (0 = top).
 * @param {{ counter: number }} groups - Shared counter for sibling-group ids.
 * @returns {object[]} Items: { text, runs, level, ordered, ordinal, group, style }.
 */
function parseListItems(listEl, level, groups) {
  const ordered = listEl.tagName.toLowerCase() === 'ol';
  const startAttr = parseInt(listEl.getAttribute('start'), 10);
  const start = Number.isFinite(startAttr) && startAttr > 0 ? startAttr : 1;
  groups.counter += 1;
  const group = `g${groups.counter}`;
  const items = [];
  let ordinal = start;
  Array.from(listEl.children).forEach((li) => {
    if (li.tagName.toLowerCase() !== 'li') return;
    const runs = stripManualBullet(extractTextWithFormatting(li, { skipLists: true }), ordered);
    const text = runs.map((r) => r.text).join('');
    if (text.trim()) {
      items.push({ text: normalizeText(text), runs, level, ordered, ordinal, group, style: extractElementStyle(li) });
      ordinal += 1;
    }
    Array.from(li.children).forEach((child) => {
      const tag = child.tagName.toLowerCase();
      if (tag === 'ul' || tag === 'ol') items.push(...parseListItems(child, Math.min(level + 1, 8), groups));
    });
  });
  return items;
}

/**
 * Removes a typed bullet glyph (or a typed number in an ordered list) from the start of a list
 * item, because PowerPoint draws its own.
 * @param {object[]} runs - Text runs.
 * @param {boolean} ordered - Whether the list is numbered.
 * @returns {object[]} Runs without the leading glyph.
 */
function stripManualBullet(runs, ordered) {
  if (!runs.length) return runs;
  const re = ordered ? MANUAL_NUMBER_RE : MANUAL_BULLET_RE;
  // Matched on the joined text, so a glyph in its own run ('<strong>•</strong> Item') counts too.
  const joined = runs.map((r) => r.text).join('');
  const match = re.exec(joined);
  if (!match || !match[0]) return runs;
  return sliceRuns(runs, match[0].length, joined.length).filter((r) => r.text);
}

/**
 * A plain string without a typed bullet glyph at its start ('• Title' -> 'Title'). A value such
 * as '-5 degrees' is kept, because a dash only counts as a glyph when a space follows it.
 * @param {string} text - Text (already normalised).
 * @returns {string} Text without the glyph.
 */
function stripLeadingBulletGlyph(text) {
  const match = MANUAL_BULLET_RE.exec(text || '');
  return match && match[0] ? text.slice(match[0].length).trim() : text;
}

/**
 * Paragraph runs whose lines start with typed bullet glyphs, as real bullet paragraphs (a model
 * value like '• point' placed in a <p> slot). Null when the paragraph does not start with one.
 * Lines without a glyph stay with the bullet above them, as a soft line break.
 * @param {object[]} runs - Paragraph runs ('\n' runs are line breaks).
 * @returns {object[][]|null} Runs per bullet item, glyphs removed.
 */
function typedBulletItems(runs) {
  const joined = runs.map((r) => r.text).join('');
  if (!MANUAL_BULLET_RE.test(joined)) return null;
  const items = [];
  let lineStart = 0;
  const lines = [];
  for (let i = 0; i <= joined.length; i += 1) {
    if (i === joined.length || joined[i] === '\n') {
      lines.push({ start: lineStart, end: i });
      lineStart = i + 1;
    }
  }
  lines.forEach(({ start, end }) => {
    const lineRuns = sliceRuns(runs, start, end);
    const text = lineRuns.map((r) => r.text).join('');
    if (!text.trim()) return;
    const glyph = MANUAL_BULLET_RE.exec(text);
    if (glyph || !items.length) {
      const stripped = glyph ? sliceRuns(lineRuns, glyph[0].length, text.length) : lineRuns;
      const clean = tidyRuns(stripped);
      if (clean.length) items.push(clean);
    } else {
      items[items.length - 1] = tidyRuns([...items[items.length - 1], { text: '\n', formatting: {} }, ...lineRuns]);
    }
  });
  return items.length ? items : null;
}

/**
 * Text runs with inline formatting. Spaces between runs and `<br>` line breaks are kept
 * (a `<br>` becomes a '\n' run); HTML whitespace is collapsed.
 * @param {Element} element - Element to read.
 * @param {{ skipLists?: boolean }} [opts] - skipLists: ignore nested `ul`/`ol`.
 * @returns {{ text: string, formatting: object }[]} Runs.
 */
function extractTextWithFormatting(element, opts = {}) {
  const parts = [];

  function processNode(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.replace(/\s+/g, ' ');
      if (text) parts.push({ text, formatting: getFormattingFromAncestors(node, element) });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const tag = node.tagName.toLowerCase();
    if (tag === 'br') {
      parts.push({ text: '\n', formatting: {} });
      return;
    }
    if (tag === 'img' || tag === 'script' || tag === 'style') return;
    if (opts.skipLists && (tag === 'ul' || tag === 'ol')) return;
    Array.from(node.childNodes).forEach(processNode);
  }

  processNode(element);
  return tidyRuns(parts);
}

/**
 * Trims the run list: no leading/trailing spaces or breaks, no double spaces across runs, no
 * spaces around line breaks, no empty runs.
 * @param {{ text: string, formatting: object }[]} parts - Raw runs.
 * @returns {{ text: string, formatting: object }[]} Clean runs.
 */
function tidyRuns(parts) {
  const out = [];
  parts.forEach((part) => {
    let { text } = part;
    const prev = out[out.length - 1];
    const prevText = prev ? prev.text : '';
    if (text === '\n') {
      if (prev) prev.text = prevText.replace(/ +$/, '');
      out.push({ text: '\n', formatting: {} });
      return;
    }
    if (!prev || /[ \n]$/.test(prevText)) text = text.replace(/^ +/, '');
    if (text) out.push({ text, formatting: part.formatting || {} });
  });
  // Drop leading / trailing breaks and spaces.
  while (out.length && (out[0].text === '\n' || !out[0].text.trim())) out.shift();
  while (out.length && (out[out.length - 1].text === '\n' || !out[out.length - 1].text.trim())) out.pop();
  if (out.length) out[out.length - 1] = { ...out[out.length - 1], text: out[out.length - 1].text.replace(/ +$/, '') };
  return out.filter((p) => p.text !== '');
}

/**
 * Inline formatting from a text node's ancestors, up to (and including) `root`.
 * @param {Node} textNode - Text node.
 * @param {Element} [root] - Element where the walk stops.
 * @returns {object} bold, italic, underline, color, fontSize, fontFace.
 */
function getFormattingFromAncestors(textNode, root) {
  const formatting = {};
  let current = textNode.parentElement;

  while (current && current.tagName) {
    const tagName = current.tagName.toLowerCase();
    const style = current.style || {};

    switch (tagName) {
      case 'strong':
      case 'b':
        formatting.bold = true;
        break;
      case 'em':
      case 'i':
        formatting.italic = true;
        break;
      case 'u':
        formatting.underline = true;
        break;
      case 'code':
        formatting.fontFace = formatting.fontFace || 'Courier New';
        break;
      default:
        break;
    }

    if (style.fontWeight === 'bold' || parseInt(style.fontWeight, 10) >= 600) formatting.bold = true;
    if (style.fontStyle === 'italic') formatting.italic = true;
    if (style.textDecoration?.includes('underline')) formatting.underline = true;
    if (style.color && !formatting.color) {
      const color = convertColorToPptx(style.color);
      if (color) formatting.color = color;
    }
    if (style.fontSize && !formatting.fontSize) {
      const size = convertFontSizeToPptx(style.fontSize);
      if (size) formatting.fontSize = size;
    }
    if (style.fontFamily && !formatting.fontFace) {
      formatting.fontFace = style.fontFamily.replace(/['"]/g, '').split(',')[0].trim();
    }

    if (current === root) break;
    current = current.parentElement;
  }

  return formatting;
}

function extractElementStyle(element) {
  const style = {};
  const computedStyle = element.style || {};

  if (computedStyle.textAlign) {
    switch (computedStyle.textAlign) {
      case 'center':
        style.align = 'center';
        break;
      case 'right':
        style.align = 'right';
        break;
      case 'justify':
        style.align = 'justify';
        break;
      default:
        style.align = 'left';
    }
  }

  if (computedStyle.backgroundColor && computedStyle.backgroundColor !== 'transparent') {
    const fill = convertColorToPptx(computedStyle.backgroundColor);
    if (fill) style.fill = fill;
  }
  if (computedStyle.marginTop) style.marginTop = parseFloat(computedStyle.marginTop);
  if (computedStyle.marginBottom) style.marginBottom = parseFloat(computedStyle.marginBottom);

  return style;
}

/**
 * Table rows of cell text. Header cells are flagged; rows are padded to the same length.
 * Colours are applied at render time from the theme.
 * @param {Element} element - `table`.
 * @returns {object} Table section.
 */
function parseTableElement(element) {
  const rows = Array.from(element.querySelectorAll('tr'));
  const tableData = rows
    .map((row) =>
      Array.from(row.querySelectorAll('td, th')).map((cell) => {
        const isHeader = cell.tagName.toLowerCase() === 'th';
        return {
          text: normalizeText(cell.textContent),
          options: { bold: isHeader, header: isHeader, fontSize: TABLE_FONT_PT, align: 'left', valign: 'top' },
        };
      })
    )
    .filter((cells) => cells.length > 0);
  const width = tableData.reduce((max, cells) => Math.max(max, cells.length), 0);
  tableData.forEach((cells) => {
    while (cells.length < width) cells.push({ text: '', options: { fontSize: TABLE_FONT_PT, align: 'left', valign: 'top' } });
  });
  return { type: 'table', data: tableData, style: extractElementStyle(element) };
}

function parseImageElement(element) {
  const widthAttr = parseFloat(element.getAttribute('width'));
  const heightAttr = parseFloat(element.getAttribute('height'));
  return {
    type: 'image',
    src: element.getAttribute('src') || element.src || '',
    alt: element.alt || '',
    width: Number.isFinite(widthAttr) && widthAttr > 0 ? widthAttr : element.width || element.naturalWidth || 0,
    height: Number.isFinite(heightAttr) && heightAttr > 0 ? heightAttr : element.height || element.naturalHeight || 0,
    style: extractElementStyle(element),
  };
}

/**
 * CSS colour to pptx hex.
 * @param {string} color - CSS colour.
 * @returns {string|null} Hex, or null when the value is not a concrete colour (so the theme
 *   colour applies instead of a hard-coded grey).
 */
function convertColorToPptx(color) {
  if (typeof color !== 'string') return null;
  const value = color.trim().toLowerCase();
  if (value.startsWith('#')) return normalizeHexColor(value);
  if (value.startsWith('rgb')) {
    const matches = value.match(/[\d.]+/g);
    if (matches && matches.length >= 3) {
      if (matches.length >= 4 && parseFloat(matches[3]) === 0) return null;
      return matches
        .slice(0, 3)
        .map((n) => Math.max(0, Math.min(255, Math.round(parseFloat(n)))).toString(16).padStart(2, '0'))
        .join('');
    }
  }
  const colorMap = {
    black: '000000',
    white: 'ffffff',
    red: 'ff0000',
    green: '008000',
    blue: '0000ff',
    yellow: 'ffff00',
    gray: '808080',
    grey: '808080',
  };
  return colorMap[value] || null;
}

function convertFontSizeToPptx(fontSize) {
  const value = String(fontSize || '');
  if (value.endsWith('px')) return Math.round(parseFloat(value) * 0.75);
  if (value.endsWith('pt')) return parseFloat(value);
  if (value.endsWith('rem') || value.endsWith('em')) return Math.round(parseFloat(value) * 12);
  return null;
}

// ---------------------------------------------------------------------------------------------
// Measuring
// ---------------------------------------------------------------------------------------------

/**
 * Characters that fit on one line.
 * @param {number} widthIn - Box width in inches (insets are subtracted here).
 * @param {number} fontSize - Points.
 * @param {boolean} bold - Bold text is wider.
 * @returns {number} Characters per line (at least 1).
 */
export function charsPerLine(widthIn, fontSize, bold = false) {
  const usablePt = Math.max(0.2, widthIn - 2 * TEXT_INSET_IN) * 72;
  const charPt = fontSize * (bold ? 0.6 : 0.55);
  return Math.max(1, Math.floor(usablePt / charPt));
}

/**
 * Greedy word wrap. Honours '\n' as a forced break; words longer than a line are cut.
 * @param {string} text - Text.
 * @param {number} maxChars - Characters per line.
 * @returns {{ start: number, end: number }[]} Character ranges of each line (at least one).
 */
export function wrapText(text, maxChars) {
  const src = String(text || '');
  const limit = Math.max(1, maxChars);
  const lines = [];
  let lineStart = 0;
  let lineLen = 0;
  let hasContent = false;
  const tokenRe = /\n|[^\s]+|[^\S\n]+/g;
  let match;
  while ((match = tokenRe.exec(src)) !== null) {
    const token = match[0];
    let at = match.index;
    if (token === '\n') {
      lines.push({ start: lineStart, end: at });
      lineStart = at + 1;
      lineLen = 0;
      hasContent = false;
    } else if (!token.trim()) {
      if (hasContent) lineLen += token.length;
    } else {
      let word = token;
      if (hasContent && lineLen + word.length > limit) {
        lines.push({ start: lineStart, end: at });
        lineStart = at;
        lineLen = 0;
        hasContent = false;
      }
      while (word.length > limit) {
        if (hasContent) {
          lines.push({ start: lineStart, end: at });
          lineStart = at;
          lineLen = 0;
          hasContent = false;
        }
        lines.push({ start: at, end: at + limit });
        at += limit;
        lineStart = at;
        word = word.slice(limit);
      }
      if (word) {
        lineLen += word.length;
        hasContent = true;
      }
    }
  }
  lines.push({ start: lineStart, end: src.length });
  // A trailing forced break or a cut word can leave an empty last line; keep at least one line.
  if (lines.length > 1 && lines[lines.length - 1].start >= src.length) lines.pop();
  return lines;
}

/**
 * Height of one line of text.
 * @param {number} fontSize - Points.
 * @returns {number} Inches.
 */
function lineHeightIn(fontSize) {
  return (fontSize * LINE_HEIGHT_FACTOR) / 72;
}

/**
 * Text width available to a paragraph inside a box.
 * @param {object} para - Paragraph.
 * @param {number} boxWidthIn - Box width.
 * @returns {number} Inches.
 */
function paragraphTextWidth(para, boxWidthIn) {
  const indent = para.bullet ? BULLET_INDENT_IN * (para.bullet.level + 1) : 0;
  return Math.max(0.5, boxWidthIn - indent);
}

/**
 * Wrapped lines of a paragraph.
 * @param {object} para - Paragraph ({ runs, fontSize, bold, bullet }).
 * @param {number} boxWidthIn - Box width.
 * @returns {{ start: number, end: number }[]} Line ranges over the paragraph text.
 */
function paragraphLines(para, boxWidthIn) {
  const text = para.runs.map((r) => r.text).join('');
  const bold = Boolean(para.bold || para.runs.some((r) => r.formatting?.bold));
  return wrapText(text, charsPerLine(paragraphTextWidth(para, boxWidthIn), para.fontSize, bold));
}

/**
 * Height a paragraph needs, in inches (lines plus spacing after).
 * @param {object} para - Paragraph.
 * @param {number} boxWidthIn - Box width.
 * @returns {number} Inches.
 */
function paragraphHeight(para, boxWidthIn) {
  return paragraphLines(para, boxWidthIn).length * lineHeightIn(para.fontSize) + (para.spaceAfterPt || 0) / 72;
}

/** Fixed height a text box adds around its paragraphs. */
const TEXT_BOX_OVERHEAD_IN = 2 * TEXT_INSET_IN + HEIGHT_SAFETY_IN;

/**
 * Height of a text box holding `paragraphs`.
 * @param {object[]} paragraphs - Paragraphs.
 * @param {number} boxWidthIn - Box width.
 * @returns {number} Inches.
 */
export function measureTextBoxHeight(paragraphs, boxWidthIn) {
  return paragraphs.reduce((sum, p) => sum + paragraphHeight(p, boxWidthIn), 0) + TEXT_BOX_OVERHEAD_IN;
}

/**
 * Runs for the character range [start, end) of the joined run text.
 * @param {object[]} runs - Runs.
 * @param {number} start - Start offset.
 * @param {number} end - End offset.
 * @returns {object[]} Sliced runs.
 */
function sliceRuns(runs, start, end) {
  const out = [];
  let offset = 0;
  runs.forEach((run) => {
    const runStart = offset;
    const runEnd = offset + run.text.length;
    offset = runEnd;
    const from = Math.max(start, runStart);
    const to = Math.min(end, runEnd);
    if (to > from) out.push({ ...run, text: run.text.slice(from - runStart, to - runStart) });
  });
  return out;
}

/**
 * Splits a paragraph after `lineCount` wrapped lines.
 * @param {object} para - Paragraph.
 * @param {number} lineCount - Lines kept in the head.
 * @param {number} boxWidthIn - Box width.
 * @returns {{ head: object, tail: object|null }} Head and remaining paragraph.
 */
function splitParagraph(para, lineCount, boxWidthIn) {
  const lines = paragraphLines(para, boxWidthIn);
  if (lineCount >= lines.length) return { head: para, tail: null };
  const total = para.runs.reduce((n, r) => n + r.text.length, 0);
  const cut = lines[lineCount].start;
  const head = { ...para, runs: tidyRuns(sliceRuns(para.runs, 0, cut)) };
  const tail = { ...para, runs: tidyRuns(sliceRuns(para.runs, cut, total)), continued: true };
  if (!tail.runs.length) return { head: para, tail: null };
  return { head, tail };
}

// ---------------------------------------------------------------------------------------------
// Sections to layout blocks
// ---------------------------------------------------------------------------------------------

/**
 * Runs from a section's content (formatted runs, a plain string, or rawText).
 * @param {object} section - Section.
 * @returns {object[]} Runs.
 */
function sectionRuns(section) {
  if (Array.isArray(section.content) && section.content.every((p) => p && typeof p.text === 'string')) {
    const runs = tidyRuns(section.content.map((p) => ({ text: p.text, formatting: p.formatting || {} })));
    if (runs.length) return runs;
  }
  const text = normalizeText(typeof section.content === 'string' ? section.content : section.rawText);
  return text ? [{ text, formatting: {} }] : [];
}

/**
 * Largest explicit font size in the runs, or the base size.
 * @param {object[]} runs - Runs.
 * @param {number} base - Base size.
 * @returns {number} Points.
 */
function maxRunFontSize(runs, base) {
  return runs.reduce((max, r) => Math.max(max, Number(r.formatting?.fontSize) || 0), base);
}

/**
 * Converts parsed sections into layout blocks.
 * @param {object} section - Section.
 * @param {object} ctx - { processedImages, align }.
 * @returns {object|null} Block, or null when the section has nothing to show.
 */
function sectionToBlock(section, ctx) {
  const align = section.style?.align || ctx.align || 'left';
  switch (section.type) {
    case 'h1':
    case 'h2':
    case 'h3': {
      // A heading never draws a bullet, so a typed glyph ('• Title') is removed rather than kept.
      const runs = tidyRuns(stripManualBullet(sectionRuns(section), false))
        .map((r) => ({ ...r, formatting: { ...r.formatting, bold: true } }));
      if (!runs.length) return null;
      const base = HEADING_FONT_PT[section.type];
      return {
        kind: 'text',
        role: 'heading',
        splittable: false,
        keepWithNext: true,
        paragraphs: [{ runs, fontSize: maxRunFontSize(runs, base), bold: true, align, bullet: null }],
      };
    }
    case 'p':
    case 'text':
    case 'block': {
      const runs = sectionRuns(section);
      if (!runs.length) return null;
      // '• point' typed into a paragraph: a real bullet (hanging indent), not a glyph in the text.
      const typed = typedBulletItems(runs);
      if (typed) {
        ctx.typedGroups = (ctx.typedGroups || 0) + 1;
        const group = `typed${ctx.typedGroups}`;
        return {
          kind: 'text',
          role: 'list',
          splittable: true,
          paragraphs: typed.map((itemRuns, i) => ({
            runs: itemRuns,
            fontSize: maxRunFontSize(itemRuns, BODY_FONT_PT),
            align: 'left',
            spaceAfterPt: LIST_SPACE_AFTER_PT,
            bullet: { ordered: false, level: 0, ordinal: i + 1, group },
          })),
        };
      }
      return {
        kind: 'text',
        role: 'body',
        splittable: true,
        paragraphs: [{ runs, fontSize: maxRunFontSize(runs, BODY_FONT_PT), align, bullet: null }],
      };
    }
    case 'ul':
    case 'ol': {
      const listOrdered = section.type === 'ol';
      const items = Array.isArray(section.items) ? section.items : [];
      const paragraphs = items
        .map((item, index) => {
          const ordered = typeof item.ordered === 'boolean' ? item.ordered : listOrdered;
          const runs = Array.isArray(item.runs)
            ? tidyRuns(item.runs)
            : stripManualBullet([{ text: normalizeText(item.text), formatting: {} }], ordered).filter((r) => r.text);
          if (!runs.length) return null;
          return {
            runs,
            fontSize: maxRunFontSize(runs, BODY_FONT_PT),
            align: 'left',
            spaceAfterPt: LIST_SPACE_AFTER_PT,
            bullet: {
              ordered,
              level: Number.isFinite(item.level) ? item.level : 0,
              ordinal: Number.isFinite(item.ordinal) ? item.ordinal : index + 1,
              group: item.group || 'g0',
            },
          };
        })
        .filter(Boolean);
      if (!paragraphs.length) return null;
      return { kind: 'text', role: 'list', splittable: true, paragraphs };
    }
    case 'blockquote': {
      const runs = sectionRuns(section).map((r) => ({ ...r, formatting: { ...r.formatting, italic: true } }));
      if (!runs.length) return null;
      return {
        kind: 'quote',
        role: 'quote',
        splittable: true,
        paragraphs: [{ runs, fontSize: maxRunFontSize(runs, BODY_FONT_PT), align: 'left', bullet: null }],
      };
    }
    case 'table': {
      const data = Array.isArray(section.data) ? section.data.filter((r) => Array.isArray(r) && r.length) : [];
      if (!data.length) return null;
      const colCount = data.reduce((max, r) => Math.max(max, r.length), 0);
      const rows = data.map((cells) => {
        const padded = cells.map((cell) => ({
          text: normalizeText(typeof cell === 'string' ? cell : cell?.text),
          header: Boolean(cell?.options?.header || cell?.options?.bold),
        }));
        while (padded.length < colCount) padded.push({ text: '', header: false });
        return padded;
      });
      const headerRow = rows[0].every((cell) => cell.header) ? rows[0] : null;
      return { kind: 'table', header: headerRow, rows: headerRow ? rows.slice(1) : rows, colCount };
    }
    case 'image': {
      const image = resolveImage(section, ctx.processedImages);
      if (!image) {
        console.warn('Image not available for slide export:', String(section.src || '').slice(0, 60));
        return null;
      }
      return { kind: 'image', ...image };
    }
    default:
      return null;
  }
}

/**
 * Pixel size of a PNG or GIF data URL (other formats return null).
 * @param {string} dataUrl - data: URL.
 * @returns {{ width: number, height: number }|null} Size.
 */
function dataUrlImageSize(dataUrl) {
  try {
    const comma = dataUrl.indexOf(',');
    if (comma < 0 || !/;base64$/i.test(dataUrl.slice(0, comma))) return null;
    const head = atob(dataUrl.slice(comma + 1, comma + 1 + 64));
    const byte = (i) => head.charCodeAt(i) & 0xff;
    if (byte(0) === 0xff && byte(1) === 0xd8) return jpegSize(dataUrl.slice(comma + 1));
    if (head.slice(1, 4) === 'PNG') {
      const width = (byte(16) << 24) | (byte(17) << 16) | (byte(18) << 8) | byte(19);
      const height = (byte(20) << 24) | (byte(21) << 16) | (byte(22) << 8) | byte(23);
      return width > 0 && height > 0 ? { width, height } : null;
    }
    if (head.slice(0, 3) === 'GIF') {
      const width = byte(6) | (byte(7) << 8);
      const height = byte(8) | (byte(9) << 8);
      return width > 0 && height > 0 ? { width, height } : null;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Pixel size of a base64 JPEG from its SOFn marker (scans at most the first 512 KB).
 * @param {string} base64 - Base64 data (no data: prefix).
 * @returns {{ width: number, height: number }|null} Size.
 */
function jpegSize(base64) {
  // Base64 length must be a multiple of 4 for atob.
  const chunk = base64.slice(0, Math.min(base64.length, 700000) - (Math.min(base64.length, 700000) % 4));
  const bin = atob(chunk);
  const b = (i) => bin.charCodeAt(i) & 0xff;
  let i = 2;
  while (i + 9 < bin.length) {
    if (b(i) !== 0xff) return null;
    const marker = b(i + 1);
    if (marker === 0xff) {
      i += 1;
      continue;
    }
    const isSof = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
    if (isSof) {
      const height = (b(i + 5) << 8) | b(i + 6);
      const width = (b(i + 7) << 8) | b(i + 8);
      return width > 0 && height > 0 ? { width, height } : null;
    }
    if (marker === 0xd9 || marker === 0xda) return null;
    i += 2 + ((b(i + 2) << 8) | b(i + 3));
  }
  return null;
}

/**
 * Display size of an image: both given sizes win; one given size keeps the natural aspect ratio;
 * no given size uses the natural size.
 * @param {number} givenW - Width from the HTML (0 when missing).
 * @param {number} givenH - Height from the HTML (0 when missing).
 * @param {{ width: number, height: number }|null} natural - Natural pixel size, when known.
 * @returns {{ widthPx: number, heightPx: number }} Size (0 for unknown).
 */
export function imageDisplaySize(givenW, givenH, natural) {
  const w = Number(givenW) > 0 ? Number(givenW) : 0;
  const h = Number(givenH) > 0 ? Number(givenH) : 0;
  const nat = natural && natural.width > 0 && natural.height > 0 ? natural : null;
  if (w && h) return { widthPx: w, heightPx: h };
  if (w && nat) return { widthPx: w, heightPx: (w * nat.height) / nat.width };
  if (h && nat) return { widthPx: (h * nat.width) / nat.height, heightPx: h };
  if (nat) return { widthPx: nat.width, heightPx: nat.height };
  return { widthPx: w, heightPx: h };
}

/**
 * Image data and size for an image section: from the in-memory image cache, or straight from a
 * data: URL (self-contained, so it no longer depends on the cache).
 * @param {object} section - Image section.
 * @param {Map} processedImages - Output of preprocessImages.
 * @returns {{ data: string, widthPx: number, heightPx: number }|null} Image.
 */
function resolveImage(section, processedImages) {
  const cached = processedImages && processedImages.get(section.src);
  const data = cached?.data || (typeof section.src === 'string' && section.src.startsWith('data:image/') ? section.src : null);
  if (!data) return null;
  const fromCache = cached && cached.naturalWidth > 0 && cached.naturalHeight > 0
    ? { width: Number(cached.naturalWidth), height: Number(cached.naturalHeight) }
    : null;
  const natural = fromCache || dataUrlImageSize(data);
  const { widthPx, heightPx } = imageDisplaySize(
    Number(section.width) || Number(cached?.width) || 0,
    Number(section.height) || Number(cached?.height) || 0,
    natural
  );
  return { data, widthPx, heightPx };
}

// ---------------------------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------------------------

/**
 * Title box for a slide: font size steps down (and wraps) until the title fits; a title that
 * still does not fit is shortened and its full text is moved into the body.
 * @param {string} title - Title text.
 * @param {boolean} large - Title or section slide.
 * @returns {{ text: string, fontSize: number, h: number, overflowText: string|null }} Title box.
 */
function layoutTitle(title, large) {
  const steps = large
    ? [{ size: 36, lines: 2 }, { size: 32, lines: 2 }, { size: 28, lines: 3 }]
    : [{ size: 28, lines: 2 }, { size: 24, lines: 2 }, { size: 20, lines: 3 }];
  for (const step of steps) {
    const lines = wrapText(title, charsPerLine(CONTENT_WIDTH_IN, step.size, true)).length;
    if (lines <= step.lines) {
      return { text: title, fontSize: step.size, h: lines * lineHeightIn(step.size) + TEXT_BOX_OVERHEAD_IN, overflowText: null };
    }
  }
  const last = steps[steps.length - 1];
  const lines = wrapText(title, charsPerLine(CONTENT_WIDTH_IN, last.size, true));
  const cut = lines[last.lines - 1].end;
  const short = `${title.slice(0, Math.max(1, cut - 2)).replace(/\s+\S*$/, '').trim() || title.slice(0, cut - 2)}…`;
  return {
    text: short,
    fontSize: last.size,
    h: last.lines * lineHeightIn(last.size) + TEXT_BOX_OVERHEAD_IN,
    overflowText: title,
  };
}

/**
 * Starts a slide frame.
 * @param {string} title - Title text ('' for none).
 * @param {boolean} large - Use the title / section slide layout.
 * @param {boolean} continuation - Whether this is a "(continued)" slide.
 * @returns {object} Frame.
 */
function newFrame(title, large, continuation) {
  const frame = { title: null, elements: [], cursorY: TOP_IN, continuation, layout: large ? 'title' : 'content' };
  if (title) {
    const box = layoutTitle(title, large);
    const y = large ? Math.max(TOP_IN, Math.min(1.6, CONTENT_BOTTOM_IN - box.h - 1)) : TOP_IN;
    frame.title = { kind: 'title', x: MARGIN_X_IN, y, w: CONTENT_WIDTH_IN, h: box.h, text: box.text, fontSize: box.fontSize };
    frame.titleOverflow = box.overflowText;
    frame.cursorY = y + box.h + (large ? 0.2 : BLOCK_GAP_IN);
  }
  return frame;
}

/**
 * Height of the smallest piece of a block that could start a slide (used to keep headings with
 * the content after them).
 * @param {object} block - Block.
 * @returns {number} Inches.
 */
function minimumLeadHeight(block) {
  if (!block) return 0;
  if (block.kind === 'text' || block.kind === 'quote') {
    const first = block.paragraphs[0];
    const lines = block.splittable ? Math.min(2, paragraphLines(first, CONTENT_WIDTH_IN).length) : paragraphLines(first, CONTENT_WIDTH_IN).length;
    return lines * lineHeightIn(first.fontSize) + TEXT_BOX_OVERHEAD_IN;
  }
  if (block.kind === 'table') return 0.5;
  if (block.kind === 'image') return MIN_IMAGE_HEIGHT_IN;
  return 0;
}

/**
 * Places as much of a text or quote block as fits in `avail` inches.
 * @param {object} block - Block.
 * @param {number} y - Top of the free space.
 * @param {number} avail - Free height.
 * @param {boolean} fresh - The slide has no content yet (something must be placed).
 * @returns {{ elements: object[], height: number, rest: object|null }} Placement.
 */
function placeTextBlock(block, y, avail, fresh) {
  const isQuote = block.kind === 'quote';
  const pad = isQuote ? QUOTE_PAD_IN : 0;
  const textX = MARGIN_X_IN + (isQuote ? QUOTE_BAR_IN + 0.15 : 0);
  const textW = CONTENT_WIDTH_IN - (isQuote ? QUOTE_BAR_IN + 0.3 : 0);
  const overhead = TEXT_BOX_OVERHEAD_IN + 2 * pad;
  const room = avail - overhead;

  const taken = [];
  let used = 0;
  let rest = null;
  for (let i = 0; i < block.paragraphs.length; i += 1) {
    const para = block.paragraphs[i];
    const h = paragraphHeight(para, textW);
    if (used + h <= room) {
      taken.push(para);
      used += h;
      continue;
    }
    // Split inside the paragraph: body text and quotes always may; list items only when a single
    // item is taller than a whole slide.
    const mayCutLines = block.splittable && (block.role !== 'list' || (fresh && taken.length === 0));
    if (mayCutLines) {
      const lineH = lineHeightIn(para.fontSize);
      const fitLines = Math.floor((room - used - (para.spaceAfterPt || 0) / 72) / lineH);
      const minLines = fresh && taken.length === 0 ? 1 : 2;
      if (fitLines >= minLines) {
        const { head, tail } = splitParagraph(para, fitLines, textW);
        taken.push(head);
        used += paragraphHeight(head, textW);
        const remaining = tail ? [tail, ...block.paragraphs.slice(i + 1)] : block.paragraphs.slice(i + 1);
        rest = remaining.length ? { ...block, paragraphs: remaining } : null;
        break;
      }
    }
    rest = { ...block, paragraphs: block.paragraphs.slice(i) };
    break;
  }

  if (!taken.length) {
    if (!fresh) return { elements: [], height: 0, rest: block };
    // Nothing fits even on an empty slide (a giant heading or a single huge line): place the
    // first paragraph in all the room there is, with shrink on overflow.
    const [first, ...others] = block.paragraphs;
    const h = Math.max(0.3, avail);
    return {
      elements: buildTextElements(block, [first], textX, y, textW, h, pad, isQuote),
      height: h,
      rest: others.length ? { ...block, paragraphs: others } : null,
    };
  }

  const h = used + overhead;
  return { elements: buildTextElements(block, taken, textX, y, textW, h, pad, isQuote), height: h, rest };
}

/**
 * Elements for a placed text block (a quote also gets its background and accent bar).
 * @returns {object[]} Elements.
 */
function buildTextElements(block, paragraphs, textX, y, textW, h, pad, isQuote) {
  const boxH = Math.min(h, CONTENT_BOTTOM_IN - y);
  const elements = [];
  if (isQuote) {
    elements.push({ kind: 'quoteBackground', x: MARGIN_X_IN, y, w: CONTENT_WIDTH_IN, h: boxH });
    elements.push({ kind: 'quoteBar', x: MARGIN_X_IN, y, w: QUOTE_BAR_IN, h: boxH });
  }
  elements.push({ kind: 'text', role: block.role, x: textX, y: y + pad, w: textW, h: Math.max(0.2, boxH - 2 * pad), paragraphs });
  return elements;
}

/**
 * Table row height for the given column width and font size.
 * @param {object[]} row - Cells.
 * @param {number} colW - Column width.
 * @param {number} fontSize - Points.
 * @returns {number} Inches.
 */
function tableRowHeight(row, colW, fontSize) {
  const lines = row.reduce((max, cell) => Math.max(max, tableCellLines(cell, colW, fontSize).length), 1);
  return lines * lineHeightIn(fontSize) + TABLE_ROW_OVERHEAD_IN;
}

/** Height a table row adds around its text lines. */
const TABLE_ROW_OVERHEAD_IN = TABLE_CELL_PAD_Y_IN + 0.04;
/** Smallest table font the layout steps down to. */
const MIN_TABLE_FONT_PT = 8;

/**
 * Wrapped lines of one table cell.
 * @param {{ text: string, header?: boolean }} cell - Cell.
 * @param {number} colW - Column width.
 * @param {number} fontSize - Points.
 * @returns {{ start: number, end: number }[]} Line ranges.
 */
function tableCellLines(cell, colW, fontSize) {
  return wrapText(cell.text, charsPerLine(Math.max(0.3, colW - TABLE_CELL_PAD_X_IN + 2 * TEXT_INSET_IN), fontSize, cell.header));
}

/**
 * Splits a table row after `lineCount` wrapped lines of every cell. The head fits the space; the
 * tail (cells that had more text, others empty) continues as the next row.
 * @param {object[]} row - Cells.
 * @param {number} lineCount - Lines kept per cell.
 * @param {number} colW - Column width.
 * @param {number} fontSize - Points.
 * @returns {{ head: object[], tail: object[]|null }} Head row and remaining row.
 */
function splitTableRow(row, lineCount, colW, fontSize) {
  let hasTail = false;
  const head = [];
  const tail = [];
  row.forEach((cell) => {
    const lines = tableCellLines(cell, colW, fontSize);
    if (lines.length <= lineCount) {
      head.push(cell);
      tail.push({ ...cell, text: '' });
      return;
    }
    const cut = lines[lineCount].start;
    head.push({ ...cell, text: cell.text.slice(0, cut).trimEnd() });
    tail.push({ ...cell, text: cell.text.slice(cut).trimStart(), continued: true });
    hasTail = true;
  });
  return { head, tail: hasTail ? tail : null };
}

/**
 * Places as many table rows as fit; the header row repeats on continuation slides.
 * @returns {{ elements: object[], height: number, rest: object|null }} Placement.
 */
function placeTableBlock(block, y, avail, fresh) {
  const colW = CONTENT_WIDTH_IN / block.colCount;
  const baseFont = block.colCount > 7 ? 9 : block.colCount > 5 ? 10 : TABLE_FONT_PT;
  const header = block.header;
  let fontSize = baseFont;
  const headerH = header ? tableRowHeight(header, colW, fontSize) : 0;
  const rows = [];
  const rowH = [];
  let used = headerH;
  for (let i = 0; i < block.rows.length; i += 1) {
    const h = tableRowHeight(block.rows[i], colW, fontSize);
    if (used + h > avail) break;
    rows.push(block.rows[i]);
    rowH.push(h);
    used += h;
  }
  let remaining = block.rows.slice(rows.length);
  if (!rows.length && block.rows.length) {
    if (!fresh) return { elements: [], height: 0, rest: block };
    // One row taller than a slide: shrink the font, and when it still does not fit, split the
    // row's text: the lines that fit stay here, the rest continues as the first row of the next
    // slide (header repeated). The row height is never set below what its text needs, because
    // PowerPoint grows such a row past the bottom of the slide.
    const row = block.rows[0];
    while (fontSize > MIN_TABLE_FONT_PT && headerH + tableRowHeight(row, colW, fontSize) > avail) fontSize -= 1;
    const full = tableRowHeight(row, colW, fontSize);
    if (headerH + full <= avail) {
      // The row fits at the smaller font: it is placed here, so it must not also stay in the rest.
      rows.push(row);
      rowH.push(full);
      remaining = block.rows.slice(1);
    } else {
      const fitLines = Math.max(1, Math.floor((avail - headerH - TABLE_ROW_OVERHEAD_IN) / lineHeightIn(fontSize)));
      const { head, tail } = splitTableRow(row, fitLines, colW, fontSize);
      rows.push(head);
      rowH.push(tableRowHeight(head, colW, fontSize));
      remaining = tail ? [tail, ...block.rows.slice(1)] : block.rows.slice(1);
    }
    used = headerH + rowH[0];
  }
  if (!rows.length && !header) return { elements: [], height: 0, rest: null };
  if (!rows.length && header && headerH > avail && !fresh) return { elements: [], height: 0, rest: block };

  const allRows = header ? [header, ...rows] : rows;
  const allH = header ? [headerH, ...rowH] : rowH;
  const height = Math.min(used, avail);
  return {
    elements: [{ kind: 'table', x: MARGIN_X_IN, y, w: CONTENT_WIDTH_IN, h: height, rows: allRows, rowH: allH, colW, fontSize, hasHeader: Boolean(header) }],
    height,
    rest: remaining.length ? { ...block, rows: remaining } : null,
  };
}

/**
 * Places an image, scaled to fit. It moves to the next slide when only a sliver is left.
 * @returns {{ elements: object[], height: number, rest: object|null }} Placement.
 */
function placeImageBlock(block, y, avail, fresh, areaHeight) {
  const maxW = Math.min(CONTENT_WIDTH_IN * 0.8, 6);
  const maxH = Math.min(3, areaHeight);
  let w = block.widthPx ? block.widthPx / 96 : maxW;
  let h = block.heightPx ? block.heightPx / 96 : (block.widthPx ? w * DEFAULT_IMAGE_ASPECT : maxW * DEFAULT_IMAGE_ASPECT);
  if (!block.widthPx && block.heightPx) w = h / DEFAULT_IMAGE_ASPECT;
  const scaleTo = (limitW, limitH) => {
    const scale = Math.min(1, limitW / w, limitH / h);
    return { w: w * scale, h: h * scale };
  };
  ({ w, h } = scaleTo(maxW, maxH));
  if (h > avail) {
    const fitsReasonably = avail >= MIN_IMAGE_HEIGHT_IN && avail >= h * 0.6;
    if (!fitsReasonably && !fresh) return { elements: [], height: 0, rest: block };
    ({ w, h } = scaleTo(maxW, Math.max(0.2, avail)));
  }
  return {
    elements: [{ kind: 'image', x: MARGIN_X_IN + (CONTENT_WIDTH_IN - w) / 2, y, w, h, data: block.data }],
    height: h,
    rest: null,
  };
}

/**
 * Places (part of) a block at `y`.
 * @returns {{ elements: object[], height: number, rest: object|null }} Placement.
 */
function placeBlock(block, y, avail, fresh, areaHeight) {
  if (block.kind === 'table') return placeTableBlock(block, y, avail, fresh);
  if (block.kind === 'image') return placeImageBlock(block, y, avail, fresh, areaHeight);
  return placeTextBlock(block, y, avail, fresh);
}

/**
 * Parsed sections of a slide definition.
 * @param {object} slideDef - { title?, type?, content: html string | sections[] }.
 * @returns {object[]} Sections.
 */
function slideSections(slideDef) {
  if (typeof slideDef?.content === 'string') return parseHtmlSectionsEnhanced(slideDef.content);
  if (Array.isArray(slideDef?.content)) return slideDef.content.filter(Boolean);
  return [];
}

/**
 * Lays out one slide definition into one or more slide frames, continuing on "(continued)"
 * slides when the content does not fit. Pure: no pptxgenjs calls.
 *
 * @param {object} slideDef - Slide definition.
 * @param {number} index - Slide index.
 * @param {{ processedImages?: Map }} [ctx] - Layout context.
 * @returns {object[]} Frames: { title, elements, continuation, layout }.
 */
export function layoutSlide(slideDef, index, ctx = {}) {
  const sections = slideSections(slideDef);
  const large = slideDef?.type === 'title_slide' || slideDef?.type === 'section_header';

  const titleSection = sections.find((s) => ['h1', 'h2', 'h3'].includes(s.type));
  const rawTitle = normalizeText(slideDef?.title) || normalizeText(titleSection?.rawText) || '';
  const body = sections.filter((s) => !(s === titleSection && normalizeText(s.rawText) === rawTitle));
  // Slide titles never draw a bullet, so a typed glyph ('• Title') is removed, here and on the
  // "(continued)" titles built from it.
  const title = stripLeadingBulletGlyph(rawTitle);

  const blockCtx = { processedImages: ctx.processedImages || new Map(), align: large ? 'center' : null };
  const blocks = body.map((s) => sectionToBlock(s, blockCtx)).filter(Boolean);

  const frames = [];
  let frame = newFrame(title, large, false);
  if (frame.titleOverflow) {
    blocks.unshift({
      kind: 'text',
      role: 'body',
      splittable: true,
      paragraphs: [{ runs: [{ text: frame.titleOverflow, formatting: { bold: true } }], fontSize: BODY_FONT_PT, bold: true, align: 'left', bullet: null }],
    });
  }
  const continuedTitle = title ? `${title} ${CONTINUED_SUFFIX}` : CONTINUED_SUFFIX;

  const queue = blocks.slice();
  let steps = 0;
  while (queue.length && steps < MAX_LAYOUT_STEPS) {
    steps += 1;
    const block = queue.shift();
    const fresh = frame.elements.length === 0;
    const avail = CONTENT_BOTTOM_IN - frame.cursorY;
    const areaHeight = CONTENT_BOTTOM_IN - (frame.title ? frame.title.y + frame.title.h + BLOCK_GAP_IN : TOP_IN);
    let placed = placeBlock(block, frame.cursorY, avail, fresh, areaHeight);

    // Keep a heading with at least the start of what follows it.
    if (block.keepWithNext && !fresh && !placed.rest && queue.length) {
      const needed = placed.height + BLOCK_GAP_IN + minimumLeadHeight(queue[0]);
      if (needed > avail) placed = { elements: [], height: 0, rest: block };
    }

    if (placed.elements.length) {
      frame.elements.push(...placed.elements);
      frame.cursorY += placed.height + BLOCK_GAP_IN;
    }
    if (placed.rest) {
      queue.unshift(placed.rest);
      if (placed.elements.length || !fresh) {
        frames.push(frame);
        frame = newFrame(continuedTitle, false, true);
      }
    }
  }
  frames.push(frame);
  // A slide hidden in the template stays hidden in the deck (its continuation slides too).
  const hidden = slideDef?.hidden === true;
  return frames.map(({ title: t, elements, continuation, layout }) => ({
    title: t,
    elements,
    continuation,
    layout,
    sourceIndex: index,
    ...(hidden ? { hidden: true } : {}),
  }));
}

/**
 * Lays out a whole deck.
 * @param {object[]} slides - Slide definitions.
 * @param {{ processedImages?: Map }} [ctx] - Layout context.
 * @returns {object[]} Frames in order.
 */
export function layoutDeck(slides, ctx = {}) {
  const safe = Array.isArray(slides) ? slides : [];
  return safe.flatMap((slideDef, i) => layoutSlide(slideDef, i, ctx));
}

// ---------------------------------------------------------------------------------------------
// Rendering (pptxgenjs)
// ---------------------------------------------------------------------------------------------

/**
 * pptxgenjs run options from parsed formatting.
 * @param {object} formatting - Parsed formatting.
 * @returns {object} Run options.
 */
function runFormatting(formatting = {}) {
  const out = {};
  if (formatting.bold) out.bold = true;
  if (formatting.italic) out.italic = true;
  if (formatting.underline) out.underline = { style: 'sng' };
  const color = normalizeHexColor(formatting.color);
  if (color) out.color = color;
  if (formatting.fontSize) out.fontSize = formatting.fontSize;
  if (formatting.fontFace) out.fontFace = formatting.fontFace;
  return out;
}

/**
 * Ordered-list numbering start per sibling group for the paragraphs in one text box, so a list
 * continued on the next slide keeps counting.
 * @param {object[]} paragraphs - Paragraphs.
 * @returns {Map<string, number>} group -> start number.
 */
function numberingStarts(paragraphs) {
  const starts = new Map();
  paragraphs.forEach((p) => {
    if (p.bullet?.ordered && !starts.has(p.bullet.group)) starts.set(p.bullet.group, p.bullet.ordinal);
  });
  return starts;
}

/**
 * pptxgenjs text-run array for paragraphs: one paragraph per item, the bullet set once on the
 * first run of a bullet paragraph, a line break after the last run, `<br>` as a soft break.
 * @param {object[]} paragraphs - Paragraphs.
 * @param {object} base - { fontSize?, color, fontFace }.
 * @returns {object[]} Text runs.
 */
export function paragraphsToTextRuns(paragraphs, base) {
  const starts = numberingStarts(paragraphs);
  const out = [];
  paragraphs.forEach((para, pIndex) => {
    const pieces = [];
    let pendingBreaks = 0;
    para.runs.forEach((run) => {
      String(run.text).split('\n').forEach((piece, k) => {
        if (k > 0) pendingBreaks += 1;
        if (!piece) return;
        if (pieces.length) {
          // Each extra break is an empty line: an empty run after its own soft break.
          for (let extra = 1; extra < pendingBreaks; extra += 1) {
            pieces.push({ text: '', formatting: run.formatting || {}, softBreakBefore: true });
          }
        }
        pieces.push({ text: piece, formatting: run.formatting || {}, softBreakBefore: pendingBreaks > 0 && pieces.length > 0 });
        pendingBreaks = 0;
      });
    });
    if (!pieces.length) return;
    pieces.forEach((piece, i) => {
      const options = {
        fontSize: para.fontSize || base.fontSize || BODY_FONT_PT,
        color: base.color,
        ...(base.fontFace ? { fontFace: base.fontFace } : {}),
        ...(para.bold ? { bold: true } : {}),
        ...runFormatting(piece.formatting),
        // pptxgenjs starts a new paragraph whenever `align` changes, so every run carries it.
        align: para.align || 'left',
      };
      if (i === 0) {
        if (para.bullet) {
          options.bullet = para.bullet.ordered
            ? { type: 'number', numberStartAt: starts.get(para.bullet.group) || 1 }
            : true;
          if (para.bullet.level > 0) options.indentLevel = para.bullet.level;
        }
        if (para.spaceAfterPt) options.paraSpaceAfter = para.spaceAfterPt;
      }
      if (piece.softBreakBefore) options.softBreakBefore = true;
      if (i === pieces.length - 1 && pIndex < paragraphs.length - 1) options.breakLine = true;
      out.push({ text: piece.text, options });
    });
  });
  return out;
}

/**
 * Adds one laid-out frame to the presentation.
 * @param {PptxGenJS} pptx - Presentation.
 * @param {object} frame - Frame from layoutSlide.
 * @param {object} theme - Resolved theme.
 */
function renderFrame(pptx, frame, theme) {
  const slide = pptx.addSlide();
  if (frame.hidden) slide.hidden = true;
  if (theme.backgroundColor && theme.backgroundColor !== 'ffffff') {
    slide.background = { color: theme.backgroundColor };
  }
  const fontFace = (face) => (face ? { fontFace: face } : {});
  const rectShape = (pptx.ShapeType && pptx.ShapeType.rect) || 'rect';

  if (frame.title) {
    const t = frame.title;
    slide.addText(t.text, {
      x: t.x,
      y: t.y,
      w: t.w,
      h: t.h,
      fontSize: t.fontSize,
      bold: true,
      color: theme.titleColor,
      ...fontFace(theme.titleFont),
      align: 'center',
      valign: frame.layout === 'title' ? 'bottom' : 'middle',
      margin: TEXT_INSET_PT,
      fit: 'shrink',
    });
  }

  frame.elements.forEach((el) => {
    switch (el.kind) {
      case 'text': {
        const heading = el.role === 'heading';
        const isQuote = el.role === 'quote';
        const color = heading ? theme.headingColor : isQuote ? theme.quoteText : theme.bodyColor;
        const face = heading ? theme.titleFont || theme.bodyFont : theme.bodyFont;
        slide.addText(paragraphsToTextRuns(el.paragraphs, { color, fontFace: face }), {
          x: el.x,
          y: el.y,
          w: el.w,
          h: el.h,
          valign: 'top',
          margin: TEXT_INSET_PT,
          fit: 'shrink',
          color,
          ...fontFace(face),
        });
        break;
      }
      case 'quoteBackground':
        slide.addShape(rectShape, { x: el.x, y: el.y, w: el.w, h: el.h, fill: { color: theme.quoteFill }, line: { color: theme.quoteFill, width: 0 } });
        break;
      case 'quoteBar':
        slide.addShape(rectShape, { x: el.x, y: el.y, w: el.w, h: el.h, fill: { color: theme.quoteBar }, line: { color: theme.quoteBar, width: 0 } });
        break;
      case 'table': {
        const rows = el.rows.map((row, rIndex) =>
          row.map((cell) => {
            const header = el.hasHeader && rIndex === 0;
            return {
              text: cell.text,
              options: {
                bold: header,
                fill: { color: header ? theme.tableHeaderFill : theme.tableBodyFill },
                color: header ? theme.tableHeaderText : theme.bodyColor,
                fontSize: el.fontSize,
                ...fontFace(theme.bodyFont),
                align: 'left',
                valign: 'top',
              },
            };
          })
        );
        slide.addTable(rows, {
          x: el.x,
          y: el.y,
          w: el.w,
          h: el.h,
          colW: el.rows[0].map(() => el.colW),
          rowH: el.rowH,
          fontSize: el.fontSize,
          border: { type: 'solid', pt: 0.75, color: theme.tableBorder },
          autoPage: false,
        });
        break;
      }
      case 'image':
        slide.addImage({ data: el.data, x: el.x, y: el.y, w: el.w, h: el.h });
        break;
      default:
        break;
    }
  });
}

/**
 * Makes the pptxgenjs output stricter OOXML: pptxgenjs writes an `a:pPr` before every run of a
 * multi-run paragraph (only the first is allowed), and gives tables and text boxes clashing shape
 * ids. Both can make PowerPoint offer a repair.
 * @param {string} xml - Slide XML.
 * @returns {string} Fixed slide XML.
 */
export function sanitizeSlideXml(xml) {
  let fixed = xml.replace(/<a:p>([\s\S]*?)<\/a:p>/g, (whole, inner) => {
    const lead = inner.match(/^\s*(<a:pPr\b[^>]*\/>|<a:pPr\b[^>]*>[\s\S]*?<\/a:pPr>)/);
    const head = lead ? lead[0] : '';
    const rest = inner.slice(head.length).replace(/<a:pPr\b[^>]*\/>|<a:pPr\b[^>]*>[\s\S]*?<\/a:pPr>/g, '');
    return `<a:p>${head}${rest}</a:p>`;
  });
  let nextId = 1;
  fixed = fixed.replace(/<p:cNvPr id="\d+"/g, () => {
    const tag = `<p:cNvPr id="${nextId}"`;
    nextId += 1;
    return tag;
  });
  return fixed;
}

/**
 * Applies {@link sanitizeSlideXml} to every slide of a .pptx package.
 * @param {ArrayBuffer|Uint8Array} data - Package bytes.
 * @returns {Promise<ArrayBuffer>} Fixed package bytes.
 */
async function sanitizePptxPackage(data) {
  const zip = await JSZip.loadAsync(data);
  const slidePaths = Object.keys(zip.files).filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p));
  await Promise.all(
    slidePaths.map(async (path) => {
      const xml = await zip.file(path).async('string');
      zip.file(path, sanitizeSlideXml(xml));
    })
  );
  return zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE', mimeType: PPTX_MIME });
}

class EnhancedPptxService {
  /**
   * Builds the deck in memory.
   * @param {object[]} slides - Slide definitions ({ title?, type?, content }).
   * @param {object} [options] - Theme options (`theme` = stored template theme; explicit colour
   *   and font options override it).
   * @returns {Promise<{ pptx: PptxGenJS, frames: object[], theme: object, report: object }>} Deck.
   */
  async buildPresentation(slides, options = {}) {
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_16x9';
    const theme = resolveDeckTheme(options);
    const safeSlides = Array.isArray(slides) ? slides : [];
    const processedImages = await this.preprocessImages(safeSlides);
    const frames = layoutDeck(safeSlides, { processedImages });
    frames.forEach((frame) => renderFrame(pptx, frame, theme));
    if (frames.length === 0) pptx.addSlide();
    const report = {
      sourceSlides: safeSlides.length,
      slides: Math.max(1, frames.length),
      continuationSlides: frames.filter((f) => f.continuation).length,
      themeApplied: theme.fromStoredTheme,
    };
    return { pptx, frames, theme, report };
  }

  /**
   * Builds the deck and returns the .pptx bytes (sanitised).
   * @param {object[]} slides - Slide definitions.
   * @param {object} [options] - Export options.
   * @returns {Promise<{ data: ArrayBuffer, report: object }>} Package bytes and layout report.
   */
  async buildPptxBuffer(slides, options = {}) {
    const { pptx, report } = await this.buildPresentation(slides, options);
    const raw = await pptx.write({ outputType: 'arraybuffer' });
    let data = raw;
    try {
      data = await sanitizePptxPackage(raw);
    } catch (error) {
      console.warn('PowerPoint clean-up step failed; saving the unmodified file.', error);
    }
    return { data, report };
  }

  /**
   * Hands the finished file to the browser.
   * @param {ArrayBuffer} data - Package bytes.
   * @param {string} fileName - File name.
   */
  async savePptx(data, fileName) {
    saveAs(new Blob([data], { type: PPTX_MIME }), fileName);
  }

  /**
   * Export slide definitions to a .pptx download.
   * @param {object[]} slides - Slide definitions.
   * @param {string} fileName - File name.
   * @param {object} [options] - Export options (see buildPresentation).
   * @returns {Promise<boolean>} True when the file was produced.
   */
  async exportSlidesToPptx(slides, fileName, options = {}) {
    const { data } = await this.buildPptxBuffer(slides, options);
    await this.savePptx(data, fileName);
    return true;
  }

  /**
   * Enhanced HTML to PowerPoint conversion
   */
  async exportHtmlToPptx(htmlContent, fileName, options = {}) {
    const slides = this.slidesFromHtml(htmlContent, options);
    return this.exportSlidesToPptx(slides, fileName, options);
  }

  /**
   * Slide definitions for plain document HTML (grouped by headings).
   * @param {string} htmlContent - HTML.
   * @param {object} [options] - Grouping options.
   * @returns {object[]} Slide definitions.
   */
  slidesFromHtml(htmlContent, options = {}) {
    return this.convertSectionsToSlides(parseHtmlSectionsEnhanced(htmlContent), options);
  }

  /**
   * Preprocess images for PowerPoint
   */
  async preprocessImages(slides) {
    const imageMap = new Map();

    for (const slide of slides) {
      if (slide && slide.content) {
        const images = this.extractImagesFromContent(slide.content);

        for (const img of images) {
          if (typeof img.src === 'string' && img.src.startsWith('data:')) {
            const imageId = this.getImageIdFromSrc(img.src);
            const imageData = imageService.getImage(imageId);

            if (imageData) {
              // Given and natural sizes are kept apart so one given size keeps the aspect ratio.
              imageMap.set(img.src, {
                data: imageData.dataUrl,
                width: img.width || 0,
                height: img.height || 0,
                naturalWidth: imageData.width || 0,
                naturalHeight: imageData.height || 0,
                alt: img.alt || imageData.name,
              });
            }
          }
        }
      }
    }

    return imageMap;
  }

  /**
   * Convert HTML sections to slide definitions
   */
  convertSectionsToSlides(sections, options = {}) {
    const slides = [];
    let currentSlide = {
      title: '',
      content: [],
    };

    const maxSectionsPerSlide = options.maxSectionsPerSlide || 4;
    let sectionsInCurrentSlide = 0;

    for (const section of sections) {
      if (
        section.type === 'h1' ||
        (section.type === 'h2' && sectionsInCurrentSlide > 0) ||
        sectionsInCurrentSlide >= maxSectionsPerSlide
      ) {
        if (currentSlide.content.length > 0) {
          slides.push({ ...currentSlide });
        }

        currentSlide = {
          title: section.type.startsWith('h') ? section.rawText : '',
          content: [section],
        };
        sectionsInCurrentSlide = 1;
      } else {
        currentSlide.content.push(section);
        sectionsInCurrentSlide++;
      }
    }

    if (currentSlide.content.length > 0) {
      slides.push(currentSlide);
    }

    return slides;
  }

  /**
   * Extract images from slide content
   */
  extractImagesFromContent(content) {
    if (typeof content === 'string') {
      return imageService.extractImagesFromHtml(content);
    }

    if (Array.isArray(content)) {
      return content.filter((section) => section && section.type === 'image');
    }

    return [];
  }

  /**
   * Get image ID from data URL
   */
  getImageIdFromSrc(src) {
    if (src.includes('data-image-id')) {
      const match = src.match(/data-image-id="([^"]+)"/);
      return match ? match[1] : null;
    }

    const allImages = imageService.getAllImages();
    const matchingImage = allImages.find((img) => img.dataUrl === src);
    return matchingImage ? matchingImage.id : null;
  }
}

export { parseHtmlSectionsEnhanced };

const enhancedPptxService = new EnhancedPptxService();
export default enhancedPptxService;
